import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ScrollText, Search, Download, RefreshCw, Phone, Mail, Calendar,
  User, RotateCw, CheckCircle2, AlertTriangle, ExternalLink, Copy, Check,
} from 'lucide-react';
import { API, adminAuthHeader } from '../api';
import VoucherPreviewModal from './VoucherPreviewModal';
import { fmtDate } from '../utils/dateFormat';

const fmtDateShort = (iso) => fmtDate(iso) || '—';

const VoucherRegister = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [copied, setCopied] = useState('');
  const [preview, setPreview] = useState(null);
  const [reissuingId, setReissuingId] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = { };
      if (q.trim()) params.q = q.trim();
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await axios.get(`${API}/admin/vouchers`, {
        params,
        headers: adminAuthHeader(),
      });
      setRows(res.data || []);
    } catch (_) { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);           // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setTimeout(load, 250);          // debounce search + filter
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, statusFilter]);

  const counts = useMemo(() => {
    const c = { all: rows.length, issued: 0, draft: 0 };
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  const totalVoided = useMemo(() => rows.reduce((n, r) => n + (r.voided_count || 0), 0), [rows]);

  const downloadVoucher = async (r) => {
    if (!r.id) return;
    const res = await axios.get(`${API}/admin/itineraries/${r.id}/voucher-pdf`, { headers: adminAuthHeader(), responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a'); a.href = url;
    a.download = `Voucher-${r.voucher_no || r.id}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 200);
    // Reload so a fresh voucher_no shows up if this was the first download.
    setTimeout(load, 800);
  };

  const emailVoucher = (r) => {
    // Same mailto pattern as ItineraryBuilder; real Mailjet delivery pending keys.
    const to = r.customer_email || '';
    const subject = `Trip Voucher - ${r.trip_title || 'Your booking'} (${r.voucher_no || ''})`;
    const dates = r.start_date && r.end_date ? `${fmtDate(r.start_date)} to ${fmtDate(r.end_date)}` : 'your dates';
    const dest = (r.destinations || []).join(', ') || 'your trip';
    const body =
`Hi ${r.customer_name || 'there'},

Please find attached your official Trip Voucher for ${dest} (${dates}).
Booking ID: ${r.voucher_no || ''}

Warm regards,
Team Travelsera`;
    window.location.href = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const shareVoucherWA = (r) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const url = r.voucher_no ? `${origin}/api/share/voucher/${r.voucher_no}` : '';
    const dest = (r.destinations || []).join(', ') || 'your trip';
    const dates = r.start_date && r.end_date ? `${fmtDate(r.start_date)} to ${fmtDate(r.end_date)}` : 'your dates';
    const text =
`*Trip Voucher for ${r.customer_name || 'You'}* ✈️

Your booking is confirmed 🎉
🌴 *${dest}*
📅 ${dates}
🎟️ Voucher ${r.voucher_no || ''}

Tap to open your official Trip Voucher (PDF):
${url}

_Team Travelsera 💛_`;
    const phone = (r.customer_phone || '').replace(/\D/g, '');
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
  };

  const reissue = async (r) => {
    // Ask for a short reason for the audit trail — stored on the voided entry.
    const reason = window.prompt(
      `Reissue voucher ${r.voucher_no || '(none)'} — what is the reason?\n\n` +
      '(e.g. "Dates changed", "Hotel swapped", "Extended by 2 days")\n' +
      'This reason is stored on the old voucher entry for audit and is not shown to the customer.'
    );
    if (reason === null) return;              // user cancelled
    const clean = reason.trim();
    if (!clean) {
      window.alert('A short reason is required to reissue a voucher.');
      return;
    }
    setReissuingId(r.id);
    try {
      await axios.post(
        `${API}/admin/itineraries/${r.id}/reissue-voucher`,
        { reason: clean },
        { headers: adminAuthHeader() },
      );
      await load();
    } catch (_) { window.alert('Could not reissue. Please try again.'); }
    finally { setReissuingId(''); }
  };

  const copyShareUrl = (r) => {
    if (!r.voucher_no) return;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const url = `${origin}/api/share/voucher/${r.voucher_no}`;
    navigator.clipboard?.writeText(url);
    setCopied(r.voucher_no);
    setTimeout(() => setCopied(''), 1200);
  };

  const previewIt = (r) => {
    setPreview({
      id: r.id,
      trip_title: r.trip_title,
      customer: { name: r.customer_name, phone: r.customer_phone, email: r.customer_email },
      destinations: r.destinations,
      start_date: r.start_date,
      end_date: r.end_date,
    });
  };

  return (
    <div data-testid="voucher-register">
      {/* Heading + summary */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ScrollText size={20} className="text-[#0d64a8]" /> Voucher Register
          </h2>
          <p className="text-sm text-gray-500 mt-1">Every trip voucher issued by Travelsera. Search by voucher number, phone, name or trip.</p>
        </div>
        <button onClick={load} className="text-xs px-3 py-1.5 border border-gray-200 rounded-md hover:border-[#0d64a8] hover:text-[#0d64a8] flex items-center gap-1" data-testid="voucher-register-refresh">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* Stat chips */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {[
          { key: 'all', label: 'Total', tone: 'gray' },
          { key: 'issued', label: 'Issued', tone: 'green' },
          { key: 'draft', label: 'Drafts', tone: 'amber' },
        ].map((s) => {
          const active = statusFilter === s.key;
          return (
            <button
              key={s.key}
              onClick={() => setStatusFilter(s.key)}
              data-testid={`vr-filter-${s.key}`}
              className={`text-left border rounded-lg px-4 py-3 transition ${active ? 'border-[#0d64a8] bg-[#e8f2fb] shadow-sm' : 'border-gray-200 bg-white hover:border-[#0d64a8]/40'}`}
            >
              <div className="text-[11px] uppercase tracking-widest text-gray-500">{s.label}</div>
              <div className="text-2xl font-bold text-gray-900 mt-0.5">{counts[s.key] ?? 0}</div>
            </button>
          );
        })}
        <div className="border border-gray-200 bg-white rounded-lg px-4 py-3">
          <div className="text-[11px] uppercase tracking-widest text-gray-500 flex items-center gap-1"><AlertTriangle size={11}/> Voided</div>
          <div className="text-2xl font-bold text-orange-600 mt-0.5">{totalVoided}</div>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search voucher no, phone, customer name, trip title..."
          className="w-full pl-9 pr-9 py-2.5 border border-gray-200 rounded-md text-sm focus:border-[#0d64a8]"
          data-testid="vr-search"
        />
        {q && (
          <button onClick={() => setQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-700">Clear</button>
        )}
      </div>

      {/* Rows table */}
      {loading ? (
        <div className="text-center py-16 text-gray-500 text-sm">Loading vouchers…</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
          <ScrollText className="mx-auto text-gray-300 mb-2" size={38} />
          <p className="text-gray-600 font-medium">No vouchers match your search</p>
          <p className="text-sm text-gray-400 mt-1">Generate a voucher from any itinerary to see it here.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200 text-[11px] uppercase tracking-widest text-gray-500 font-semibold">
            <div className="col-span-2">Voucher No.</div>
            <div className="col-span-3">Customer</div>
            <div className="col-span-2">Phone</div>
            <div className="col-span-2">Trip Dates</div>
            <div className="col-span-1 text-center">Status</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {rows.map((r) => (
            <div
              key={r.id}
              data-testid={`vr-row-${r.id}`}
              className="grid grid-cols-2 md:grid-cols-12 gap-3 items-center px-4 py-3.5 border-b last:border-b-0 border-gray-100 hover:bg-gray-50 transition"
            >
              <div className="col-span-2 md:col-span-2 min-w-0">
                <div className="text-sm font-mono font-bold text-[#0d64a8]">{r.voucher_no || '—'}</div>
                {r.voucher_issued_at && <div className="text-[10px] text-gray-400 mt-0.5">Issued {fmtDateShort(r.voucher_issued_at)}</div>}
                {r.voided_count > 0 && (
                  <div
                    className="text-[10px] text-orange-600 mt-0.5 flex items-center gap-1"
                    title={(r.voided_history || []).map((v) => `${v.voucher_no}: ${v.reason || '(no reason logged)'}`).join('\n')}
                  >
                    <AlertTriangle size={9}/> {r.voided_count} voided{r.voided_history?.[r.voided_history.length - 1]?.reason ? ` · ${r.voided_history[r.voided_history.length - 1].reason.slice(0, 40)}` : ''}
                  </div>
                )}
              </div>
              <div className="col-span-2 md:col-span-3 min-w-0">
                <div className="flex items-center gap-1.5">
                  <User size={12} className="text-gray-400 shrink-0" />
                  <span className="text-sm text-gray-900 font-medium truncate">{r.customer_name || '—'}</span>
                </div>
                <div className="text-[11px] text-gray-500 truncate ml-4">{r.trip_title || (r.destinations || []).join(', ') || '—'}</div>
              </div>
              <div className="col-span-2 md:col-span-2 min-w-0">
                <div className="text-sm text-gray-700 font-mono flex items-center gap-1"><Phone size={11} className="text-gray-400"/> {r.customer_phone || '—'}</div>
                {r.customer_email && (
                  <div className="text-[11px] text-gray-400 flex items-center gap-1 truncate"><Mail size={10}/> {r.customer_email}</div>
                )}
              </div>
              <div className="col-span-2 md:col-span-2 text-xs text-gray-600">
                <div className="flex items-center gap-1"><Calendar size={11} className="text-gray-400"/> {fmtDate(r.start_date) || '—'}</div>
                <div className="text-gray-400 pl-4">to {fmtDate(r.end_date) || '—'}</div>
              </div>
              <div className="col-span-1 md:col-span-1 text-center">
                {r.status === 'issued' ? (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-green-50 text-green-700 font-semibold"><CheckCircle2 size={10}/> Issued</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-gray-100 text-gray-500 font-semibold">Draft</span>
                )}
              </div>
              <div className="col-span-2 md:col-span-2 flex justify-end items-center gap-1">
                <button
                  onClick={() => previewIt(r)}
                  data-testid={`vr-preview-${r.id}`}
                  title="Preview voucher"
                  className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#0d64a8] hover:text-[#0d64a8]"
                >
                  <ExternalLink size={13} />
                </button>
                <button
                  onClick={() => downloadVoucher(r)}
                  data-testid={`vr-download-${r.id}`}
                  title="Download PDF"
                  className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#0d64a8] hover:text-[#0d64a8]"
                >
                  <Download size={13} />
                </button>
                {r.voucher_no && (
                  <button
                    onClick={() => copyShareUrl(r)}
                    data-testid={`vr-copy-${r.id}`}
                    title="Copy public share URL"
                    className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#0d64a8] hover:text-[#0d64a8]"
                  >
                    {copied === r.voucher_no ? <Check size={13} className="text-green-500"/> : <Copy size={13} />}
                  </button>
                )}
                {r.voucher_no && (
                  <button
                    onClick={() => reissue(r)}
                    disabled={reissuingId === r.id}
                    data-testid={`vr-reissue-${r.id}`}
                    title="Void this voucher and issue a fresh one"
                    className="w-8 h-8 rounded-md border border-orange-200 flex items-center justify-center text-orange-500 hover:bg-orange-50 disabled:opacity-50"
                  >
                    {reissuingId === r.id ? <RefreshCw size={13} className="animate-spin"/> : <RotateCw size={13} />}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <VoucherPreviewModal
        it={preview}
        open={!!preview}
        onClose={() => setPreview(null)}
        onDownload={(it) => downloadVoucher(rows.find((r) => r.id === it.id) || it)}
        onEmail={(it) => emailVoucher(rows.find((r) => r.id === it.id) || it)}
        onWhatsApp={(it) => shareVoucherWA(rows.find((r) => r.id === it.id) || it)}
      />
    </div>
  );
};

export default VoucherRegister;
